import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Typography,
} from '@mui/material';
import PeopleView from '../components/database/PeopleView';
import CompanyView from '../components/database/CompanyView';
import StatisticsView from '../components/database/StatisticsView';
import IndustryTagView from '../components/database/IndustryTagView';
import VerificationStatusView from '../components/database/VerificationStatusView';
import CountryView from '../components/database/CountryView';
import StateView from '../components/database/StateView';
import LogsView from '../components/database/LogsView';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`database-tabpanel-${index}`}
      aria-labelledby={`database-tab-${index}`}
      style={{ flex: 1, overflowX: 'hidden', display: value === index ? 'flex' : 'none' }}
      {...other}
    >
      {value === index && <Box sx={{ width: '100%', overflowX: 'hidden', pt: 0.5 }}>{children}</Box>}
    </div>
  );
}

const TABS = [
  'companies',
  'people',
  'industry-tags',
  'verification',
  'states',
  'countries',
  'statistics',
  'logs'
];

const DatabasePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Parse path to find the current active tab index
  const pathParts = location.pathname.split('/');
  const subPath = pathParts[pathParts.length - 1];
  const activeTabIdx = TABS.indexOf(subPath);
  const tabValue = activeTabIdx !== -1 ? activeTabIdx : 0;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    navigate(`/database/${TABS[newValue]}`);
  };

  React.useEffect(() => {
    // If user lands on the base /database URL, automatically redirect to /database/companies
    if (location.pathname === '/database' || location.pathname === '/database/') {
      navigate('/database/companies', { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%',
      minHeight: '100vh',
      display: 'flex', 
      flexDirection: 'column', 
      overflowX: 'hidden' 
    }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: 1, 
        borderColor: 'divider', 
        flexShrink: 0, 
        mb: 1, 
        ml: -1.5,
        pr: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', pl: 1.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary', mr: 2 }}>
            Database
          </Typography>
        </Box>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="database tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            minHeight: '36px',
            '& .MuiTabs-flexContainer': {
              gap: 0.5
            },
            '& .MuiTab-root': {
              minHeight: '36px',
              py: 0.5,
              px: 1.5,
              fontSize: '0.8125rem',
              fontWeight: 500,
              textTransform: 'none'
            }
          }}
        >
          <Tab label="Companies" />
          <Tab label="People" />
          <Tab label="Industry Tags" />
          <Tab label="Verification Status" />
          <Tab label="States" />
          <Tab label="Countries" />
          <Tab label="Statistics" />
          <Tab label="Logs" />
        </Tabs>
      </Box>
      {tabValue === 0 && (
      <TabPanel value={tabValue} index={0}>
        <CompanyView />
      </TabPanel>
      )}
      {tabValue === 1 && (
      <TabPanel value={tabValue} index={1}>
        <PeopleView />
      </TabPanel>
      )}
      {tabValue === 2 && (
      <TabPanel value={tabValue} index={2}>
        <IndustryTagView />
      </TabPanel>
      )}
      {tabValue === 3 && (
      <TabPanel value={tabValue} index={3}>
        <VerificationStatusView />
      </TabPanel>
      )}
      {tabValue === 4 && (
      <TabPanel value={tabValue} index={4}>
        <StateView />
      </TabPanel>
      )}
      {tabValue === 5 && (
      <TabPanel value={tabValue} index={5}>
        <CountryView />
      </TabPanel>
      )}
      {tabValue === 6 && (
      <TabPanel value={tabValue} index={6}>
        <StatisticsView />
      </TabPanel>
      )}
      {tabValue === 7 && (
      <TabPanel value={tabValue} index={7}>
        <LogsView />
      </TabPanel>
      )}
    </Box>
  );
};

export default DatabasePage;
